import { useCallback, useState, useEffect } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import { GET_USER_DATA } from '../../../../../config/apiPaths';
import { getUrlParams, getUrls, processApiPath } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';

interface TroubleshootingContainerState {
    troubleshootingData?: Record<string, any>; // result of the GET_CLASS_DATA_PATH api call
}

type LoadingStatus = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

const useTroubleshootingContainer = (state: TroubleshootingContainerState = {}) => {
    console.log('TroubleshootingContainer');
    const { api } = getUrls();

    const [troubleshootingData, setTroubleshootingData] = useState<any>();

    const { t: tCommon } = useTranslation('common');

    const { authMode } = useContainer(AdminAuthContainer);

    const search = new URLSearchParams(window.location.search);
    const troubleshootingUuid =
        search.get('troubleshooting_uuid') || window.localStorage.getItem('troubleshooting_uuid');

    const [getTroubleshootingStatus, setGetTroubleshootingStatus] = useState<LoadingStatus>('INITIAL');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { troubleshootingId: troubleshootingIdV1 } = useParams<{ troubleshootingId?: string }>();
    const { troubleshooting_id: troubleshootingIdV2 } = getUrlParams();
    const troubleshootingId = troubleshootingIdV1 || troubleshootingIdV2 || '';

    const returnErrorMessage = useCallback(
        (error: any): string => {
            console.error(error);
            return tCommon('errorMessage');
        },
        [tCommon]
    );

    const getTroubleshootingDataCall = useCallback(async () => {
        const { data } = await axios.get(
            processApiPath(GET_USER_DATA, undefined, {
                troubleshooting_uuid: troubleshootingUuid!,
                troubleshooting_id: troubleshootingId,
                auth_mode: authMode
            })
        );

        if (data?.success === false) {
            throw Error(data?.error);
        }
        return data;
    }, [troubleshootingUuid, troubleshootingId, authMode]);

    const getTroubleshooting = useCallback(async () => {
        setGetTroubleshootingStatus('LOADING');

        try {
            const data = state.troubleshootingData ?? (await getTroubleshootingDataCall());

            setTroubleshootingData(data);
            setGetTroubleshootingStatus('SUCCESS');
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setGetTroubleshootingStatus('ERROR');
        }
    }, [state.troubleshootingData, getTroubleshootingDataCall, returnErrorMessage]);

    useEffect(() => {
        if (troubleshootingId && troubleshootingUuid) {
            getTroubleshooting();
        }
    }, [troubleshootingId, getTroubleshooting, troubleshootingUuid]);

    return {
        troubleshootingId,
        api,
        troubleshootingData,
        getTroubleshootingStatus,
        errorMessage
    };
};

const TroubleshootingContainer = createContainer(useTroubleshootingContainer);

export default TroubleshootingContainer;
